/**
 * W1 统一世界状态（`GET /world/state`，契约 `WorldState`）→ 地图视图模型。
 * 只做形状转换，不补、不猜：position 为 null 就不画点；leg 缺出发 / 到达时间或几何不足两点，就不沿线插值（用服务端给的 position）；
 * pose 原样透传（作息接不上时后端给 idle，不是 sleeping）。坐标仍是 WGS-84，画到高德上时由 AmapView 换算。
 */
import { TransportModeValues, type TransportMode, type WorldPetState, type WorldState } from "@/shared/contracts";
import type { TravelMode, MapLeg, WorldPet, WorldScene } from "./model";

function travelMode(mode: string): TravelMode {
  if (mode === "walk" || mode === "taxi" || mode === "drive") return mode;
  if (mode === "transit" || mode === "bus") return "bus";
  return "other";
}

/** W1 leg.mode 原样对上契约的交通方式；认不出（后端新加了别的）就是 null——不猜成走路或开车。 */
function transportMode(mode: string | null | undefined): TransportMode | null {
  return mode && (TransportModeValues as readonly string[]).includes(mode) ? (mode as TransportMode) : null;
}

function time(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function legOf(pet: WorldPetState): MapLeg | null {
  if (!pet.leg || pet.leg.route.length < 2) return null;
  const departsAt = time(pet.leg.departs_at);
  const arrivesAt = time(pet.leg.arrives_at);
  if (departsAt == null || arrivesAt == null) return null;
  return { mode: travelMode(pet.leg.mode), route: pet.leg.route, departsAt, arrivesAt };
}

/**
 * 头像：W1 的 `avatar_url` 是证件照上方裁好的 256 小图，没有就是 null（后端不拿别的图冒充）。
 * 那时前端退回这只宠物在家庭资料里的照片——和全站其他头像同一张（PetAvatar / PetPortrait 用的 photo_url）；
 * 再没有才是爪印。`fallbackPhoto` 只来自当前账号的家庭资料，别人的宠物没有这一项。
 */
export function worldPetFromState(pet: WorldPetState, fallbackPhoto: string | null = null): WorldPet {
  const a = pet.activity;
  return {
    petId: pet.pet_id,
    name: pet.name,
    species: pet.species,
    photoUrl: pet.avatar_url ?? fallbackPhoto,
    relation: pet.relation,
    home: pet.home ? { center: pet.home.center, precisionM: pet.home.precision_m, label: pet.home.label } : null,
    activity: {
      kind: a.kind,
      phase: a.phase,
      pose: a.pose,
      title: a.title,
      doing: a.doing,
      place: a.place ? { name: a.place.name, lat: a.place.lat, lng: a.place.lng } : null,
      // 停留事实仅属于当前there阶段；非停留快照不沿用旧住处。缺字段不补事实。
      overnight: a.phase === "there" ? a.overnight : undefined,
      stayPlace: a.phase === "there" ? a.stay_place : null,
      since: time(a.since),
      until: time(a.until),
      job: a.job,
      journeyId: a.journey_id ?? null,
      visitId: a.visit_id ?? null,
    },
    leg: legOf(pet),
    legMode: transportMode(pet.leg?.mode),
    position: pet.position ? { lat: pet.position.lat, lng: pet.position.lng } : null,
    basis: pet.position?.basis ?? "unknown",
    version: pet.version ?? 0,
  };
}

/**
 * 地图上只画这个家里已经住进来的宠物（2026-09-25 巡检 P1）。W1 会把还在接待 / 待入住的第二只也当成“在家睡觉”返回：
 * 地图把它画出来，点它面板说“芝麻在小窝里睡觉”，可它还没住进来、换不成当前宠物，“进小窝看看”进的是当前宠物豆豆的小窝。
 * settled：已经住进来的宠物 id——live 由 HouseholdProvider 从 /households 算好（已启用的家里 join_step 为 moved_in，
 * 和 WorldGate.movedInPets 同一条判断），也就是“当前宠物”能换成的那一批：画出来的每一只，面板上的入口都换得过去。
 * 待入住的不画标记、不进面板、不参与“看哪只”（对准、?focus=、回来接着看都只在画出来的这几只里挑）。
 * W1 目前只给这个家的宠物（relation 只有 mine / household）；以后地图带上别人家的宠物（W2）时，这里要按 relation 分开算。
 */
export function settledOnly(state: WorldState, settled: ReadonlySet<string>): WorldState {
  return { ...state, pets: state.pets.filter((pet) => settled.has(pet.pet_id)) };
}

export function sceneFromWorldState(state: WorldState, householdPhotos: ReadonlyMap<string, string | null> = new Map()): WorldScene {
  return { pets: state.pets.map((pet) => worldPetFromState(pet, householdPhotos.get(pet.pet_id) ?? null)), source: "facts", catchingUp: false };
}

/** 地图先看哪一只：当前选中的家庭宠物 → 自己的第一只 → 第一只。 */
export function focusPetId(scene: WorldScene, currentPetId: string | null | undefined): string | null {
  if (currentPetId && scene.pets.some((p) => p.petId === currentPetId)) return currentPetId;
  return scene.pets.find((p) => p.relation === "mine")?.petId ?? scene.pets[0]?.petId ?? null;
}
